import {
  BuildingIcon,
  MoreHorizontalIcon,
  PencilIcon,
  Trash2Icon,
  UserRoundIcon,
} from 'lucide-react'
import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { toast } from 'sonner'
import { deleteLead, promoteLeadCustomer, type LeadSummary } from '@/api/leads'
import { useApiErrorMessage } from '@/api/errors'
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog'
import { Button } from '@/components/ui/button'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'

/**
 * Editing is for anybody who may see the lead — that is what being handed one is for. Deleting is
 * the owner's or an administrator's, and the item is hidden for everybody else rather than shown
 * and refused.
 */
export function LeadRowActions({
  lead,
  isOwner,
  onEdit,
  onAssign,
  onChanged,
}: {
  lead: LeadSummary
  /** Owner or administrator. The backend refuses the rest regardless of what is rendered. */
  isOwner: boolean
  onEdit: () => void
  /** Owner or administrator only, for the same reason the item is hidden for everybody else. */
  onAssign: () => void
  onChanged: () => void
}) {
  const { t } = useTranslation()
  const errorMessage = useApiErrorMessage()
  const [confirming, setConfirming] = useState(false)
  const [busy, setBusy] = useState(false)

  async function run(action: () => Promise<unknown>, success: string, failure: string) {
    if (busy) {
      return
    }
    setBusy(true)
    try {
      await action()
      toast.success(success)
      onChanged()
    } catch (error) {
      toast.error(errorMessage(error, failure))
    } finally {
      setBusy(false)
      setConfirming(false)
    }
  }

  return (
    <>
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button
            variant="ghost"
            size="icon-sm"
            aria-label={t('leads.actions.menu', { name: lead.companyName })}
          >
            <MoreHorizontalIcon />
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end" className="w-60">
          <DropdownMenuItem onSelect={onEdit}>
            <PencilIcon />
            {t('leads.actions.edit')}
          </DropdownMenuItem>
          {/* Only while the copy points nowhere: doing it twice is refused by the backend, and an
              item that always fails is worse than no item. */}
          {!lead.linkedToCustomer && (
            <DropdownMenuItem
              onSelect={() =>
                void run(
                  () => promoteLeadCustomer(lead.id),
                  t('leads.promote.success', { name: lead.companyName }),
                  t('leads.promote.failed'),
                )
              }
            >
              <BuildingIcon />
              {t('leads.actions.promote')}
            </DropdownMenuItem>
          )}
          {isOwner && (
            <>
              <DropdownMenuItem onSelect={onAssign}>
                <UserRoundIcon />
                {t('leads.actions.assign')}
              </DropdownMenuItem>
              <DropdownMenuSeparator />
              <DropdownMenuItem variant="destructive" onSelect={() => setConfirming(true)}>
                <Trash2Icon />
                {t('leads.actions.delete')}
              </DropdownMenuItem>
            </>
          )}
        </DropdownMenuContent>
      </DropdownMenu>

      <AlertDialog open={confirming} onOpenChange={setConfirming}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{t('leads.delete.title')}</AlertDialogTitle>
            <AlertDialogDescription>
              {t('leads.delete.description', { name: lead.companyName })}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>{t('common.cancel')}</AlertDialogCancel>
            <AlertDialogAction
              disabled={busy}
              onClick={() =>
                void run(
                  () => deleteLead(lead.id),
                  t('leads.delete.success', { name: lead.companyName }),
                  t('leads.delete.failed'),
                )
              }
            >
              {t('leads.actions.delete')}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  )
}
